import { unitToString } from '@/utils/unitUtils';
import useEcoinventData from '@/hooks/useEcoinventData';
import { XMarkIcon } from '@heroicons/react/24/outline';
import PropTypes from 'prop-types';
import { useState } from 'react';
import TableFilter from './TableFilter';
import CurrentSelection from './CurrentSelection';
import Pagination from './Pagination';

import { getGeography } from './ecoinventUtils';

const EcoinventModal = ({ onClose, onUpdate, ecoinventQuery, datasetId }) => {
  const [searchInput, setSearchInput] = useState('');

  const {
    data,
    loading,
    error,
    metadata,
    selectedItem,
    setSelectedItem,
    currentPage,
    totalPages,
    totalCount,
    handlePageChange,
    handleSearch,
    filters,
    onFilterChange,
  } = useEcoinventData(ecoinventQuery, datasetId);

  const handleUpdate = () => {
    if (selectedItem) {
      onUpdate(selectedItem);
    }
    onClose();
  };

  const handleCancel = () => {
    setSelectedItem(null);
    onClose();
  };

  const handleSearchClick = () => {
    handleSearch(searchInput);
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      handleSearchClick();
    }
  };

  return (
    <dialog className="modal" open>
      <div className="modal-box w-4/5 max-w-6xl h-[90vh] flex flex-col p-0">
        <div className="flex-shrink-0 p-4 border-b border-gray-200">
          <button className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2" onClick={handleCancel}>
            ✕
          </button>
          <h3 className="font-bold text-lg">Ecoinvent Data</h3>
        </div>

        <div className="flex-1 overflow-hidden p-6 flex flex-col gap-6">
          <CurrentSelection selectedItem={selectedItem} onClear={() => setSelectedItem(null)} />

          {error && (
            <div className="text-center text-red-500">
              <span>{error}</span>
            </div>
          )}

          <div className="flex gap-2">
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Search datasets..."
                className="input input-bordered w-full input-sm pr-8"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              {searchInput && (
                <button
                  className="absolute right-2 top-1/2 -translate-y-1/2 btn btn-ghost btn-xs btn-square"
                  onClick={() => {
                    setSearchInput('');
                    handleSearch('');
                  }}
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              )}
            </div>
            <button type="button" className="btn btn-sm h-9 btn-accent" onClick={handleSearchClick}>
              Search
            </button>
          </div>

          <div className="border border-gray-200 rounded-lg flex-1 flex flex-col min-h-0">
            <div className="overflow-y-auto overflow-x-hidden flex-1">
              <table className="table table-sm table-fixed w-full">
                <thead className="sticky top-0 bg-white">
                  <tr className="bg-gray-50">
                    <th className="text-left font-medium text-xs w-[35%]">
                      <TableFilter
                        title="Activity Name"
                        options={metadata?.activity_names || []}
                        selectedValues={filters.activityName}
                        onFilterChange={values => onFilterChange('activityName', values)}
                      />
                    </th>
                    <th className="text-left font-medium text-xs w-[12%]">
                      <TableFilter
                        title="Geography"
                        options={metadata?.geographies || []}
                        selectedValues={filters.geography}
                        onFilterChange={values => onFilterChange('geography', values)}
                      />
                    </th>
                    <th className="text-left font-medium text-xs w-[22%]">
                      <TableFilter
                        title="Reference Product"
                        options={metadata?.reference_products || []}
                        selectedValues={filters.referenceProduct}
                        onFilterChange={values => onFilterChange('referenceProduct', values)}
                      />
                    </th>
                    <th className="text-left font-medium text-xs w-[10%]">Unit</th>
                    <th className="text-left font-medium text-xs w-[11%]">kgCO2eq</th>
                    <th className="text-left font-medium text-xs w-[10%]">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="text-center py-8">
                        <span className="loading loading-spinner loading-lg"></span>
                      </td>
                    </tr>
                  ) : (
                    data.map((item, index) => (
                      <tr key={item.dataset?.id || index} className="hover:bg-gray-50">
                        <td className="text-xs font-bold">{item?.dataset?.name}</td>
                        <td className="text-xs">{getGeography(item)}</td>
                        <td className="text-xs">{item.dataset?.metadata?.ecoingent_reference_product || ''}</td>
                        <td className="text-xs">{unitToString(item.unit)}</td>
                        <td className="text-xs text-blue-600 font-medium">{item.value?.toFixed(3) || ''}</td>
                        <td>
                          {selectedItem?.dataset?.id === item?.dataset?.id ? (
                            <button className="btn btn-xs bg-green-200 hover:bg-green-200">Selected</button>
                          ) : (
                            <button
                              className="btn bg-blue-500 hover:bg-blue-800 text-white btn-xs"
                              onClick={() => setSelectedItem(item)}
                            >
                              Select
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="flex-shrink-0 p-4 border-t border-gray-200 bg-white">
          <div className="flex justify-between items-center">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={totalCount}
              onPageChange={handlePageChange}
            />
            <div className="flex gap-4">
              <button className="btn btn-secondary btn-sm" onClick={handleCancel}>
                Cancel
              </button>
              <button className="btn btn-accent btn-sm" onClick={handleUpdate} disabled={!selectedItem}>
                Update
              </button>
            </div>
          </div>
        </div>
      </div>
    </dialog>
  );
};

EcoinventModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  ecoinventQuery: PropTypes.object,
  datasetId: PropTypes.number,
};

export default EcoinventModal;
